/**
 * 客服端 WebSocket Hook
 * 连接地址: ws://host:8090/ws
 * 认证: JWT token 自动区分身份
 * 功能: 认证、心跳、消息收发、事件监听
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { getCsToken } from '@/utils/store';

// ==================== 类型定义 ====================

/** WS消息基础结构 */
interface WsMessage {
  type: string;
  [key: string]: unknown;
}

/** WS auth结果 */
interface AuthResult {
  type: 'auth_result';
  success: boolean;
  msg: string;
}

/** 新待接入推送 */
interface NewPendingPush {
  type: 'new_pending';
  humanChatId: string;
  userNickname: string;
  userAvatar: string;
  lastMsg?: string;
  waitStartTime: number;
}

/** 收到用户消息推送 */
interface UserMsgPush {
  type: 'user_msg';
  humanChatId: string;
  contentType: 'text' | 'multipart';
  content?: string;
  parts?: string;
  chatLogId: string;
}

/** 数量更新推送 */
interface CountUpdatePush {
  type: 'count_update';
  myPending?: number;
  myActive?: number;
  unassigned?: number;
}

/** 已读确认推送 */
interface ReadUpdatePush {
  type: 'read_update';
  humanChatId: string;
  unreadCount: number;
}

/** 会话结束推送 */
interface ChatClosedPush {
  type: 'chat_closed';
  humanChatId: string;
  endType: number;
  /** 结束提示文案（与用户端一致） */
  message?: string;
  /** i18n 消息 Key（前端优先使用此字段解析本地化文本） */
  messageKey?: string;
}

/** WS事件回调集合 */
interface CsWsCallbacks {
  onAuthSuccess?: () => void;
  onAuthFail?: (msg: string) => void;
  onNewPending?: (data: NewPendingPush) => void;
  onUserMsg?: (data: UserMsgPush) => void;
  onCountUpdate?: (data: CountUpdatePush) => void;
  onReadUpdate?: (data: ReadUpdatePush) => void;
  onChatClosed?: (data: ChatClosedPush) => void;
  onKick?: () => void;
  onError?: (err: Event) => void;
}

// ==================== 常量 ====================

/** WS连接地址（开发环境用当前host，生产环境用配置） */
const CS_WS_URL = import.meta.env.VITE_CS_WS_URL || '';

/** 心跳间隔（30秒） */
const HEARTBEAT_INTERVAL = 30000;

/** 重连间隔（5秒） */
const RECONNECT_INTERVAL = 5000;

/** 最大重连次数 */
const MAX_RECONNECT_COUNT = 10;

// ==================== Hook ====================

/** 客服端WebSocket连接Hook */
export default function useCsWebSocket(callbacks: CsWsCallbacks) {
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 使用 ref 存储最新的 callbacks，避免引用不稳定导致 useEffect 死循环
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  /** 获取WS连接地址 */
  const getWsUrl = useCallback(() => {
    if (CS_WS_URL) return CS_WS_URL;
    // 动态构建：当前页面host + 端口8090，路径/ws与Netty WebSocketServerProtocolHandler("/ws", true)匹配
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    return `${protocol}//${host}:8090/ws`;
  }, []);

  /** 发送JSON消息 */
  const sendMsg = useCallback((msg: WsMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  /** 发送心跳 */
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      sendMsg({ type: 'heartbeat' });
    }, HEARTBEAT_INTERVAL);
  }, [sendMsg]);

  /** 停止心跳 */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  /** 处理WS消息 */
  const handleMessage = useCallback((event: MessageEvent) => {
    const raw = event.data;
    // pong 心跳响应
    if (raw === 'pong') return;

    try {
      const msg: WsMessage = JSON.parse(raw);
      switch (msg.type) {
        case 'auth_result': {
          const authMsg = msg as unknown as AuthResult;
          if (authMsg.success) {
            setAuthenticated(true);
            callbacksRef.current.onAuthSuccess?.();
          } else {
            setAuthenticated(false);
            callbacksRef.current.onAuthFail?.(authMsg.msg);
          }
          break;
        }
        case 'new_pending':
          callbacksRef.current.onNewPending?.(msg as unknown as NewPendingPush);
          break;
        case 'user_msg':
          callbacksRef.current.onUserMsg?.(msg as unknown as UserMsgPush);
          break;
        case 'count_update':
          callbacksRef.current.onCountUpdate?.(msg as unknown as CountUpdatePush);
          break;
        case 'read_update':
          callbacksRef.current.onReadUpdate?.(msg as unknown as ReadUpdatePush);
          break;
        case 'chat_closed':
          callbacksRef.current.onChatClosed?.(msg as unknown as ChatClosedPush);
          break;
        case 'kick':
          callbacksRef.current.onKick?.();
          break;
        default:
          // 未知消息类型忽略
          break;
      }
    } catch {
      // JSON解析失败忽略
    }
  }, []);

  /** 连接WebSocket */
  const connect = useCallback(() => {
    // 关闭旧连接（如果旧连接还在 CONNECTING，不直接 close 以避免浏览器警告）
    if (wsRef.current) {
      const oldWs = wsRef.current;
      wsRef.current = null;
      if (oldWs.readyState === WebSocket.CONNECTING) {
        // 仍在连接中：清空事件处理器，让其自然结束
        oldWs.onopen = null;
        oldWs.onerror = null;
        oldWs.onclose = null;
        oldWs.onmessage = null;
      } else {
        oldWs.close();
      }
    }
    stopHeartbeat();

    const url = getWsUrl();
    const ws = new WebSocket(url);
    wsRef.current = ws;

    // 捕获当前 ws 实例，用于 onclose 中判断是否被后续连接替换（防止级联重连）
    const currentWs = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectCountRef.current = 0;
      // 连接成功后立即发送auth认证
      const token = getCsToken();
      if (token) {
        sendMsg({ type: 'auth', content: token });
      }
      startHeartbeat();
    };

    ws.onmessage = handleMessage;

    ws.onerror = (err) => {
      callbacksRef.current.onError?.(err);
    };

    ws.onclose = () => {
      // 级联重连保护：如果 wsRef.current 已被后续连接替换，跳过重连
      if (wsRef.current !== currentWs) return;

      setConnected(false);
      setAuthenticated(false);
      stopHeartbeat();
      // 自动重连
      if (reconnectCountRef.current < MAX_RECONNECT_COUNT) {
        reconnectCountRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, RECONNECT_INTERVAL);
      }
    };
  }, [getWsUrl, sendMsg, handleMessage, startHeartbeat, stopHeartbeat]);

 /** 断开连接 */
  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectCountRef.current = MAX_RECONNECT_COUNT; // 阻止重连
    stopHeartbeat();
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.CONNECTING) {
        // 仍在连接中：清空事件处理器，让其自然结束（避免浏览器 "closed before established" 警告）
        wsRef.current.onopen = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.onmessage = null;
      } else {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    setConnected(false);
    setAuthenticated(false);
  }, [stopHeartbeat]);

  /** 发送客服消息给用户 */
  const sendStaffMsg = useCallback((
    humanChatId: string,
    contentType: 'text' | 'multipart',
    content?: string | null,
    parts?: object | null,
  ) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }
    sendMsg({
      type: 'staff_msg',
      humanChatId,
      contentType,
      content,
      parts,
    });
  }, [sendMsg]);

  /** 客服关闭会话 */
  const sendStaffClose = useCallback((humanChatId: string) => {
    sendMsg({ type: 'staff_close', humanChatId });
  }, [sendMsg]);

  /** 客服标记已读 */
  const sendStaffRead = useCallback((humanChatId: string) => {
    sendMsg({ type: 'staff_read', humanChatId });
  }, [sendMsg]);

  /** 页面加载时自动连接 */
  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    connected,
    authenticated,
    sendStaffMsg,
    sendStaffClose,
    sendStaffRead,
    reconnect: connect,
    disconnect,
  };
}
